import { rgba } from "polished"
import styled, { css } from "styled-components"
import motionTokens from "styles/motionTokens"
import type { Theme } from "styles/theme"

import type { CloseButtonTone, CloseButtonVariant } from "."

const SURFACE_ALPHA = 0.16
const TRANSPARENT_HOVER_ALPHA = 0.08

const resolveColor = (theme: Theme, customColor?: string) =>
  customColor ?? theme.palette.primary.main

const surfaceStyles = css<{
  $tone: CloseButtonTone
  $customColor?: string
}>`
  background: ${({ theme, $customColor }) =>
    rgba(resolveColor(theme, $customColor), SURFACE_ALPHA)};
  color: ${({ theme, $customColor }) => resolveColor(theme, $customColor)};

  &:hover,
  &:focus-visible {
    background: ${({ theme, $customColor }) =>
      rgba(resolveColor(theme, $customColor), SURFACE_ALPHA)};
    color: ${({ theme, $customColor }) => resolveColor(theme, $customColor)};
  }
`

const transparentStyles = css<{ $customColor?: string }>`
  background: transparent;
  color: ${({ theme, $customColor }) => resolveColor(theme, $customColor)};

  &:hover,
  &:focus-visible {
    background: ${({ theme, $customColor }) =>
      rgba(resolveColor(theme, $customColor), TRANSPARENT_HOVER_ALPHA)};
    color: ${({ theme, $customColor }) => resolveColor(theme, $customColor)};
  }
`

export const Button = styled.button<{
  $size: number
  $tone: CloseButtonTone
  $variant: CloseButtonVariant
  $customColor?: string
}>`
  align-items: center;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  display: inline-flex;
  height: ${({ $size }) => `${$size}px`};
  justify-content: center;
  line-height: 0;
  padding: 0;
  width: ${({ $size }) => `${$size}px`};
  transition:
    background ${motionTokens.duration.short}ms ${motionTokens.easing.standard},
    color ${motionTokens.duration.short}ms ${motionTokens.easing.standard};

  ${({ $variant }) =>
    $variant === "surface" ? surfaceStyles : transparentStyles}

  svg {
    color: currentColor;
    transition: transform ${motionTokens.duration.short}ms
      ${motionTokens.easing.standard};
    transform-origin: center;
  }

  &:focus-visible {
    outline: none;
  }

  &:hover,
  &:focus-visible {
    svg {
      transform: scale(${motionTokens.scale.medium});
    }
  }

  &:active {
    svg {
      transform: scale(0.96);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    svg {
      transition: none;
    }

    &:hover,
    &:focus-visible,
    &:active {
      svg {
        transform: none;
      }
    }
  }
`
